import {
  PUNCTUATION_WIDE_LINE_WIDTH,
  resolvePunctuationLineBreak,
  wideCaptionLineCapacity,
  widenedCaptionBoxWidth,
} from '@src/editor/services/captionLineBreakStyle';
import { endLineAtUnit } from '@src/editor/services/captionSoftBreaks';
import { fillInWhitespacesInTranscriptElements } from '@src/editor/services/punctuation';
import {
  buildTranslatedAnimationSource,
  insertUnitLineEnds,
  wrapWideAnimationLines,
} from '@src/editor/services/translatedKaraokeAnimation';
import {
  DEFAULT_TEXT_TRANSCRIPT_CONFIG,
  FILLER_WORDS,
} from '@src/services/karaoke/karaokeConstants';
import { deepClone } from '@src/utility/deepClone';

import { TextElement } from '../elements/TextElement';

import type { KaraokeConfig } from './KaraokeConfig';
import type { TranscriptTextToken, TranscriptToken } from '@src/editor';
import type { PhraseSoftBreak } from '@src/editor/services/captionSoftBreaks';
import type { AnimationSourceUnit } from '@src/editor/services/translatedKaraokeAnimation';
import type { KaraokeLine } from '@src/editor/types/karaoke';

export const MINIMUM_KARAOKE_CLIP_DURATION = 0.1;

/**
 * PLAT-11905 — where a caption of the dub-timed set sits and how its words
 * animate, on the clip's own clock, worked out from the dub manifest every time
 * the set is derived. Held in memory only: it is never saved (no per-word
 * timestamp is stored anywhere), and the row record it came from is.
 */
export interface DubCaptionTiming {
  startSec: number;
  endSec: number;
  /** One per shown word, in reading order. `separator` is the whitespace the
   * caption's text has after the word (`''` between most Chinese or Japanese
   * words, and after the last word). `segmentId`, `charStart` and `charEnd`
   * say where the word sits in its dub line's text, which is where an edit of
   * the row record lands. */
  words: {
    text: string;
    separator: string;
    startSec: number;
    endSec: number;
    segmentId: string;
    charStart: number;
    charEnd: number;
    /** The translation fragment the word belongs to, for Fix Translation and
     * the edited-word mark; absent when it cannot be named safely. */
    leadUuid?: string;
    initialIndex?: number;
  }[];
  /** The speakers of the dub lines the caption's words come from. */
  speakers: string[];
  /**
   * The speakers of the rows the record holds between the caption before this
   * one and this one, whose words the clip cuts out. A join with the caption
   * before takes those rows in, so their speakers count in the join's
   * speaker check.
   */
  hiddenSpeakersBefore?: string[];
}

interface KaraokeClipState {
  transcriptTokens: TranscriptToken[];
  karaokeConfig: KaraokeConfig;
  hiddenTranscriptTokenIds: string[];
  startSecondsOffset?: number;
  endSecondsOffset?: number;
  ignoredByFiltersIds: string[];
  translatedText?: string;
  translatedPhrases?: {
    text: string;
    ts: number;
    endTs: number;
    originalIndices?: number[];
  }[];
  phraseOriginalIndices?: number[];
  dubRowId?: string;
  dubTiming?: DubCaptionTiming;
  softBreakAfterTokenIds?: string[];
  softBreakPhrasePositions?: PhraseSoftBreak[];
}

function cloneDubTiming(timing: DubCaptionTiming): DubCaptionTiming {
  return {
    startSec: timing.startSec,
    endSec: timing.endSec,
    words: timing.words.map(word => ({ ...word })),
    speakers: [...timing.speakers],
    ...(timing.hiddenSpeakersBefore
      ? { hiddenSpeakersBefore: [...timing.hiddenSpeakersBefore] }
      : {}),
  };
}

/**
 * PLAT-11905 — one dubbed word as a caption phrase: its own dubbed times, plus
 * its translation fragment's identity when known. That identity is in-memory
 * only, like the identity a source row's phrases get on screen; dub-timed
 * captions are never saved as clips.
 */
function dubWordPhrase(word: DubCaptionTiming['words'][number]): {
  text: string;
  ts: number;
  endTs: number;
  leadUuid?: string;
  initialIndex?: number;
} {
  return {
    text: word.text,
    ts: word.startSec,
    endTs: word.endSec,
    ...(word.leadUuid !== undefined ? { leadUuid: word.leadUuid } : {}),
    ...(word.initialIndex !== undefined
      ? { initialIndex: word.initialIndex }
      : {}),
  };
}

export class KaraokeClip extends TextElement {
  transcriptTokens: TranscriptToken[];
  hiddenTranscriptTokenIds: string[] = [];
  startSecondsOffset?: number;
  endSecondsOffset?: number;
  karaokeConfig: KaraokeConfig;
  ignoredByFiltersIds: string[] = [];
  // PLAT-9621: when set, this is the phrase-resolved translated string and
  // overrides text derived from `transcriptTokens`. Source tokens are still
  // retained on the clip so timing (start/end) and phrase-block highlight
  // (T48) can resolve from `[min(ts), max(end_ts)]` of the source words.
  translatedText?: string;
  // PLAT-9621: per-phrase units that compose `translatedText`. Each unit
  // is one TranslationPhrase with its own `[ts, endTs]` (resolved from
  // the source words at the phrase's `originalIndices`). Used by the
  // canvas to animate phrase-by-phrase inside a multi-phrase caption,
  // mirroring per-word animation on the English path. High-fidelity
  // 1:1 phrase mappings collapse to one unit per word — i.e. word-by-
  // word highlighting still happens.
  translatedPhrases?: {
    text: string;
    ts: number;
    endTs: number;
    originalIndices?: number[];
  }[];
  // PLAT-9623: source-phrase identity (from `TranslationPhrase.originalIndices`).
  // Stamped at produce time on the translated path; lets synthetic-break
  // commands resolve which clip(s) belong to a given phrase without
  // re-deriving from text matching. Undefined on the original-language path.
  phraseOriginalIndices?: number[];
  // PLAT-9623: shared id across all clips that came from the SAME
  // synthetic split (or chain of splits). Stable string — survives any
  // formatter / spread / re-derivation gymnastics that strip array
  // reference identity. Set by `AddSyntheticCaptionBreakCommand` (both
  // halves get the parent's id, or a new uuid if none) and preserved
  // by `MergeAdjacentCaptionClipsCommand`. Producer-emitted clips have
  // none — siblings only exist where the user has split.
  siblingGroupId?: string;
  // PLAT-11905: set on a caption of the dub-timed set — the row it is derived
  // from. Its times come from `dubTiming`, not from source words (it has none).
  dubRowId?: string;
  dubTiming?: DubCaptionTiming;
  // PLAT-11914: user soft line breaks — the caption stays one clip, the text
  // after each break renders on the next line (see `captionSoftBreaks.ts`).
  // Original language: ids of the tokens a line ends after. Translated:
  // positions inside `translatedPhrases` where a line starts.
  softBreakAfterTokenIds?: string[];
  softBreakPhrasePositions?: PhraseSoftBreak[];

  constructor(
    props: Partial<KaraokeClip> & {
      transcriptTokens: TranscriptToken[];
      karaokeConfig: KaraokeConfig;
    },
  ) {
    super({ ...props, type: 'text' });
    // TODO: parse tokens for state: hidden, muted_by_hideFillers, muted_by_hidePunct and add to hiddenTranscriptTokenIds
    this.transcriptTokens = props.transcriptTokens;
    this.karaokeConfig = props.karaokeConfig;
    if (props.hiddenTranscriptTokenIds) {
      this.hiddenTranscriptTokenIds = props.hiddenTranscriptTokenIds;
    }
    if (props.startSecondsOffset != null) {
      this.startSecondsOffset = props.startSecondsOffset;
    }
    if (props.endSecondsOffset != null) {
      this.endSecondsOffset = props.endSecondsOffset;
    }
    if (props.ignoredByFiltersIds) {
      this.ignoredByFiltersIds = props.ignoredByFiltersIds;
    }
    if (props.translatedText != null) {
      this.translatedText = props.translatedText;
    }
    if (props.translatedPhrases != null) {
      this.translatedPhrases = props.translatedPhrases;
    }
    if (props.phraseOriginalIndices != null) {
      this.phraseOriginalIndices = [...props.phraseOriginalIndices];
    }
    if (props.siblingGroupId != null) {
      this.siblingGroupId = props.siblingGroupId;
    }
    if (props.dubRowId != null) {
      this.dubRowId = props.dubRowId;
    }
    if (props.dubTiming != null) {
      this.dubTiming = cloneDubTiming(props.dubTiming);
    }
    if (props.softBreakAfterTokenIds?.length) {
      this.softBreakAfterTokenIds = [...props.softBreakAfterTokenIds];
    }
    if (props.softBreakPhrasePositions?.length) {
      this.softBreakPhrasePositions = props.softBreakPhrasePositions.map(b => ({
        ...b,
      }));
    }
  }

  public setKaraokeConfig(config: KaraokeConfig) {
    this.karaokeConfig = config;
  }

  public setHiddenTranscriptTokenIds(ids: string[]): void {
    this.hiddenTranscriptTokenIds = ids;
  }

  public setIgnoredByFiltersIds(ids: string[]): void {
    this.ignoredByFiltersIds = ids;
  }

  /**
   * Override getState() to include KaraokeClip-specific fields
   */
  override getState(): ReturnType<TextElement['getState']> & KaraokeClipState {
    return {
      ...super.getState(),
      transcriptTokens: deepClone(this.transcriptTokens),
      karaokeConfig: deepClone(this.karaokeConfig),
      hiddenTranscriptTokenIds: [...this.hiddenTranscriptTokenIds],
      startSecondsOffset: this.startSecondsOffset,
      endSecondsOffset: this.endSecondsOffset,
      ignoredByFiltersIds: [...this.ignoredByFiltersIds],
      translatedText: this.translatedText,
      translatedPhrases: this.translatedPhrases
        ? this.translatedPhrases.map(p => ({ ...p }))
        : undefined,
      phraseOriginalIndices: this.phraseOriginalIndices
        ? [...this.phraseOriginalIndices]
        : undefined,
      siblingGroupId: this.siblingGroupId,
      // Carried so the layout engine's clones (made before every serialise)
      // keep a dub caption's timing.
      dubRowId: this.dubRowId,
      dubTiming: this.dubTiming ? cloneDubTiming(this.dubTiming) : undefined,
      softBreakAfterTokenIds: this.softBreakAfterTokenIds
        ? [...this.softBreakAfterTokenIds]
        : undefined,
      softBreakPhrasePositions: this.softBreakPhrasePositions?.map(b => ({
        ...b,
      })),
    };
  }

  /**
   * Override the TextElement.clone() to ensure we create a KaraokeClip, not a TextElement
   */
  override clone(): KaraokeClip {
    return new KaraokeClip({
      ...this.getState(),
    });
  }

  public getAllHiddenTranscriptTokenIds(): string[] {
    return [
      ...new Set([
        ...this.hiddenTranscriptTokenIds,
        ...this.getHiddenByConfigIds(),
      ]),
    ];
  }

  public filterByKaraokeConfig(
    token: TranscriptToken,
    idx: number,
    allTokens: TranscriptToken[],
  ) {
    if (
      this.karaokeConfig.hideFillers &&
      token.type === 'punct' &&
      token.value === ',' &&
      this.isAdjacentToFillerWord(idx, allTokens)
    ) {
      return false; // Hide comma bordering a filler word destined to be hidden
    }

    if (
      this.karaokeConfig.hideFillers &&
      token.value &&
      this.hasFillerWords(token.value)
    ) {
      return false;
    }

    if (this.karaokeConfig.hideComma && token.value === ',') {
      return false;
    }
    if (this.karaokeConfig.hidePeriod && token.value === '.') {
      return false;
    }
    return true;
  }

  private isAdjacentToFillerWord(
    idx: number,
    allTokens: TranscriptToken[],
  ): boolean {
    const prevIdx = this.findPreviousNonWhitespaceTokenIndex(idx, allTokens);
    const nextIdx = this.findNextNonWhitespaceTokenIndex(idx, allTokens);

    const prev = prevIdx != null ? allTokens[prevIdx] : null;
    const next = nextIdx != null ? allTokens[nextIdx] : null;

    const isAdjacentToHiddenFiller = (t: TranscriptToken | null): boolean =>
      !!(
        t &&
        this.karaokeConfig.hideFillers &&
        t.value &&
        this.hasFillerWords(t.value) &&
        !this.ignoredByFiltersIds.includes(t.id)
      );

    return isAdjacentToHiddenFiller(prev) || isAdjacentToHiddenFiller(next);
  }

  private findPreviousNonWhitespaceTokenIndex(
    fromIdx: number,
    tokens: TranscriptToken[],
  ): number | null {
    for (let i = fromIdx - 1; i >= 0; i--) {
      const t = tokens[i];
      if (!(t.type === 'punct' && t.value === ' ')) {
        return i;
      }
    }
    return null;
  }

  private findNextNonWhitespaceTokenIndex(
    fromIdx: number,
    tokens: TranscriptToken[],
  ): number | null {
    for (let i = fromIdx + 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (!(t.type === 'punct' && t.value === ' ')) {
        return i;
      }
    }
    return null;
  }

  private getHiddenByConfigIds(): string[] {
    return this.transcriptTokens
      .filter(
        (t, idx, all) =>
          // filtered out by config
          !this.filterByKaraokeConfig(t, idx, all) &&
          // not manually restored by user
          !this.ignoredByFiltersIds.includes(t.id),
      )
      .map(t => t.id);
  }

  private hasFillerWords(text: string): boolean {
    const fillerRegex = new RegExp(`\\b(?:${FILLER_WORDS.join('|')})\\b`, 'gi');
    return fillerRegex.test(text);
  }

  private getKaraokeText(): string {
    if (this.translatedText != null) {
      return this.translatedText;
    }
    const allHiddenTokenIds = this.getAllHiddenTranscriptTokenIds();
    return fillInWhitespacesInTranscriptElements(
      this.transcriptTokens
        .filter(token => !allHiddenTokenIds.includes(token.id))
        .filter(token => token.value !== '\n'),
    )
      .map(token => token.value)
      .join('');
  }

  public getTranscriptStartTime(): number | null {
    if (this.dubTiming) return this.dubTiming.startSec;
    const firstToken = this.transcriptTokens.find(
      (token): token is TranscriptTextToken =>
        'ts' in token && token.ts !== null,
    );
    if (!firstToken) return null;
    return firstToken.ts;
  }

  public getTranscriptEndTime(): number | null {
    if (this.dubTiming) return this.dubTiming.endSec;
    const reversedTokens = [...this.transcriptTokens].reverse();
    const lastToken = reversedTokens.find(
      (token): token is TranscriptTextToken & { end_ts: number } =>
        'end_ts' in token && token.end_ts !== null,
    );
    const startTime = this.getKaraokeStartTime();
    if (!lastToken) return null;
    if (startTime == null) return null;
    return lastToken.end_ts;
  }

  public getKaraokeStartTime(): number | null {
    return Math.max(
      0,
      (this.startSecondsOffset || 0) + (this.getTranscriptStartTime() || 0),
    );
  }

  public getKaraokeDuration(): number | null {
    return Math.max(
      0,
      (this.endSecondsOffset || 0) +
        (this.getTranscriptEndTime() || 0) -
        (this.getKaraokeStartTime() || 0),
    );
  }

  // Override Element getters to use computed karaoke time/duration
  override get time(): number {
    return this.getKaraokeStartTime() ?? 0;
  }

  override get duration(): number | string | null {
    return this.getKaraokeDuration();
  }

  public getKaraokeEndTime(): number | null {
    const start = this.getKaraokeStartTime();
    const duration = this.getKaraokeDuration();
    if (start == null || duration == null) return null;
    return start + duration;
  }

  /**
   * Prepends another clip's tokens and metadata into this clip.
   * Used during reconciliation to merge orphaned replacement clips.
   */
  public prependClip(otherClip: KaraokeClip): void {
    // Prepend tokens
    this.transcriptTokens.unshift(...otherClip.transcriptTokens);

    // Merge hidden token IDs (avoid duplicates)
    for (const hiddenId of otherClip.hiddenTranscriptTokenIds) {
      if (!this.hiddenTranscriptTokenIds.includes(hiddenId)) {
        this.hiddenTranscriptTokenIds.push(hiddenId);
      }
    }

    // Preserve start offset from the prepended clip if it has one
    if (otherClip.startSecondsOffset !== undefined) {
      this.startSecondsOffset = otherClip.startSecondsOffset;
    }
  }

  /**
   * Converts transcript tokens to normalized timing for karaoke animation
   * - Shifts all word times by start offset
   * - Increases durations evenly so last word end time equals karaoke end time
   */
  private getTranscriptSourceForAnimation(
    withSoftBreaks = false,
  ): AnimationSourceUnit[] {
    // PLAT-11905: a dub caption's words animate at their own dubbed times,
    // never stretched into the caption's window.
    if (this.dubTiming) {
      const clipStart = this.getKaraokeStartTime();
      if (clipStart == null) return [];
      return this.dubTiming.words.map(word => ({
        value: word.text + word.separator,
        time: Math.max(0, word.startSec - clipStart),
        duration: Math.max(0, word.endSec - word.startSec),
      }));
    }
    // PLAT-9621: per-word splitting of translated phrases for the
    // canvas's built-in karaoke highlight is a presentation concern
    // delegated to `buildTranslatedAnimationSource`. The data layer
    // still carries phrase-level units (FM-1).
    if (this.translatedPhrases && this.translatedPhrases.length > 0) {
      const clipStart = this.getKaraokeStartTime();
      const clipEnd = this.getKaraokeEndTime();
      if (clipStart == null || clipEnd == null) return [];
      return buildTranslatedAnimationSource({
        phrases: this.translatedPhrases,
        clipStart,
        clipEnd,
        softBreaks: withSoftBreaks ? this.softBreakPhrasePositions : undefined,
      });
    }
    // Translated clip without per-phrase units (legacy state) — emit
    // one block covering the full clip so the translated text still
    // renders, just without per-phrase highlighting.
    if (this.translatedText != null) {
      const startTime = this.getKaraokeStartTime();
      const endTime = this.getKaraokeEndTime();
      if (startTime == null || endTime == null) return [];
      return [
        {
          value: this.translatedText,
          time: 0,
          duration: Math.max(0, endTime - startTime),
        },
      ];
    }

    const allHiddenTokenIds = this.getAllHiddenTranscriptTokenIds();
    const softBreakIds = new Set(
      withSoftBreaks ? (this.softBreakAfterTokenIds ?? []) : [],
    );
    // Units a soft break ends the line after (PLAT-11914).
    const lineEnds = new Set<TranscriptTextToken>();

    // append punctuation to previous text token value
    const tokens = this.transcriptTokens
      .reduce((acc: TranscriptTextToken[], token) => {
        if (
          allHiddenTokenIds.includes(token.id) ||
          // Caption text drops paragraph breaks (`getKaraokeText`); with soft
          // breaks the units become the text's line ends, so they must not
          // carry them either — only the user's breaks end a line.
          (withSoftBreaks && token.value === '\n')
        ) {
          // A break on a hidden token (a comma the template hides, a word the
          // user hid) still ends the line: after the last visible unit.
          if (softBreakIds.has(token.id) && acc.length > 0) {
            lineEnds.add(acc[acc.length - 1]);
          }
          return acc;
        }
        if (token.type === 'punct' && acc.length > 0) {
          const last = acc[acc.length - 1];
          last.value = last.value ? last.value + token.value : token.value;
        } else if (token.type === 'text') {
          acc.push({ ...token }); // avoid mutating original token
        }
        if (softBreakIds.has(token.id) && acc.length > 0) {
          lineEnds.add(acc[acc.length - 1]);
        }
        return acc;
      }, [])
      .filter(
        (t): t is TranscriptTextToken & { end_ts: number; ts: number } =>
          t.ts != null && t.end_ts != null,
      );

    const clipStartTime = this.getKaraokeStartTime();
    const clipEndTime = this.getKaraokeEndTime();

    if (tokens.length === 0 || clipStartTime == null || clipEndTime == null)
      return [];

    // Calculate the original transcript time range
    const originalStartTime = tokens[0].ts;
    const lastToken = tokens[tokens.length - 1];
    const originalEndTime = lastToken.end_ts;
    const originalDuration = originalEndTime - originalStartTime;

    // Calculate the target duration (karaoke duration)
    const targetDuration = clipEndTime - clipStartTime;

    // Calculate the scaling factor for durations
    const scaleFactor =
      originalDuration > 0 ? targetDuration / originalDuration : 1;

    const units = tokens.map(word => {
      // Shift time relative to clip start
      const relativeTime = word.ts - originalStartTime;
      const scaledTime = relativeTime * scaleFactor;

      // Scale the word duration
      const originalWordDuration = word.end_ts - word.ts;
      const scaledDuration = originalWordDuration * scaleFactor;

      return {
        value: word.value || '',
        time: scaledTime,
        duration: scaledDuration,
      };
    });
    tokens.forEach((word, i) => {
      if (lineEnds.has(word)) endLineAtUnit(units, i);
    });
    return units;
  }

  // We can create a curve of pace of words in transcript to better approximate
  // animiation of text!  Thanks ChatGPT o1-preview!
  // private calculateCubicBezierForWords(): number[] {
  //   if (this.transcriptTokens.length <= 1 || this.startTs >= this.endTs) {
  //     return [0, 0, 1, 1]; // Default linear cubic-bezier
  //   }

  //   // Normalize word timings and calculate cumulative progress
  //   const totalDuration = this.endTs - this.startTs;

  //   // Function to compute cumulative progress at normalized time t
  //   const computeCumulativeProgressAt = (t: number): number => {
  //     let cumulativeProgress = 0;

  //     for (const word of this.words) {
  //       const wordStart = (word.relativeStart - this.startTs) / totalDuration;
  //       const wordEnd = (word.relativeEnd - this.startTs) / totalDuration;

  //       if (wordEnd <= t) {
  //         // Word has ended before time t, include full duration
  //         cumulativeProgress += wordEnd - wordStart;
  //       } else if (wordStart >= t) {
  //         // Word starts after time t, do not include
  //         continue;
  //       } else {
  //         // Word is in progress at time t, include partial duration
  //         cumulativeProgress += t - wordStart;
  //       }
  //     }

  //     return cumulativeProgress; // Already normalized
  //   };

  //   // Choose t1 and t2 for solving the Bezier control points
  //   const t1 = 0.3;
  //   const t2 = 0.6;

  //   // Compute cumulative progress at t1 and t2
  //   const p1 = computeCumulativeProgressAt(t1);
  //   const p2 = computeCumulativeProgressAt(t2);

  //   // Coefficients for the Bezier curve equations
  //   const a1 = 3 * (1 - t1) * (1 - t1) * t1;
  //   const b1 = 3 * (1 - t1) * t1 * t1;
  //   const c1 = t1 * t1 * t1;

  //   const a2 = 3 * (1 - t2) * (1 - t2) * t2;
  //   const b2 = 3 * (1 - t2) * t2 * t2;
  //   const c2 = t2 * t2 * t2;

  //   // Right-hand sides of the equations
  //   const rhs1 = p1 - c1;
  //   const rhs2 = p2 - c2;

  //   // Solve the linear system to find y1 and y2
  //   const denominator = a1 * b2 - a2 * b1;
  //   if (denominator === 0) {
  //     // Fallback to linear if system cannot be solved
  //     return [0, 0, 1, 1];
  //   }

  //   const y1 = (rhs1 * b2 - rhs2 * b1) / denominator;
  //   const y2 = (a1 * rhs2 - a2 * rhs1) / denominator;

  //   // Ensure control points are within [0,1]
  //   const y1Clamped = Math.min(Math.max(y1, 0), 1);
  //   const y2Clamped = Math.min(Math.max(y2, 0), 1);

  //   // Set x1 and x2 equal to t1 and t2
  //   const x1 = t1;
  //   const x2 = t2;

  //   return [x1, y1Clamped, x2, y2Clamped];
  // }

  toKaraokeLine(): KaraokeLine {
    return {
      id: this.id,
      transcriptTokens: this.transcriptTokens,
      hiddenTranscriptTokenIds: this.hiddenTranscriptTokenIds,
      ignoredByFiltersIds: this.ignoredByFiltersIds,
      allHiddenTranscriptTokenIds: this.getAllHiddenTranscriptTokenIds(),
      transcriptStartTime: this.getTranscriptStartTime() || 0,
      transcriptEndTime: this.getTranscriptEndTime() || 0,
      startSecondsOffset: this.startSecondsOffset,
      endSecondsOffset: this.endSecondsOffset,
      karaokeConfigId: this.karaokeConfig.id,
      siblingGroupId: this.siblingGroupId,
      translatedText: this.translatedText,
      // PLAT-11905: a dub caption shows one phrase per word, at its dubbed time.
      translatedPhrases: this.dubTiming
        ? this.dubTiming.words.map(dubWordPhrase)
        : this.translatedPhrases
          ? this.translatedPhrases.map(p => ({ ...p }))
          : undefined,
      phraseOriginalIndices: this.phraseOriginalIndices
        ? [...this.phraseOriginalIndices]
        : undefined,
      ...(this.dubRowId != null ? { dubRowId: this.dubRowId } : {}),
      softBreakAfterTokenIds: this.softBreakAfterTokenIds
        ? [...this.softBreakAfterTokenIds]
        : undefined,
      softBreakPhrasePositions: this.softBreakPhrasePositions?.map(b => ({
        ...b,
      })),
    };
  }

  /** PLAT-11914 — whether the user has put a soft line break in this caption. */
  hasSoftBreaks(): boolean {
    return (
      (this.softBreakAfterTokenIds?.length ?? 0) > 0 ||
      (this.softBreakPhrasePositions?.length ?? 0) > 0
    );
  }

  /**
   * Converts the element to JSON compatible with Creatomate
   */
  toJSON(formatTime: Boolean = false): Record<string, any> {
    const _config = this.karaokeConfig;
    const {
      shadow_color,
      shadow_blur,
      shadow_x,
      shadow_y,
      transcript_color,
      transcript_effect,
      transcript_maximum_length,
      transcript_placement,
      transcript_split,
      // Editor-side segmentation setting, not a Creatomate property.
      line_break_style: _lineBreakStyle,
      // PLAT-11409 — editor-side bookkeeping only; an object-valued unknown
      // key must not leak into the Creatomate element JSON.
      font_before_substitution: _fontBeforeSubstitution,
      // PLAT-11689 — editor-side flag; reaches Creatomate as `visible` below.
      hidden,
      ...config
    } = _config;
    const time = this.getKaraokeStartTime();
    const duration = this.getKaraokeDuration();

    // Params to improve timing of word appearance.
    const bezierParams = '0,0,1,1'; // Linear

    const transcriptSource = this.getTranscriptSourceForAnimation();

    const json = {
      ...super.toJSON(formatTime),
      text: this.getKaraokeText(),
      time,
      duration,
      ...config,
      id: this.id, // to avoid override from config
      ...(hidden && { visible: false }),
      z_index: 1000, // Ensure karaoke elements appear above layout compositions
      // Never let the renderer re-split this row. To Creatomate
      // `transcript_maximum_length` is "the maximum number of characters shown
      // simultaneously" (CREATOMATE_API.md), so forwarding the line-length
      // setting made it page THIS row's text on the canvas by its own rule: a
      // row wider than the setting came back split on the canvas while the
      // transcript panel still showed it as one row, and the two views
      // disagreed. Our segmentation owns caption rows — one clip is one row,
      // and the renderer shows the row it is handed. `null` is Creatomate's
      // own default ("no maximum"); the config value stays the segmenter's
      // line-length budget (`resolveConfiguredMaxCharLength`).
      transcript_maximum_length: null,
      ...(shadow_color && {
        shadow_color,
        shadow_blur,
        shadow_x,
        shadow_y,
      }),

      animations: config.animations.map((anim: any) => ({
        ...anim,
        duration: Math.max(0.1, duration! - 0.2),
        fade: false,
        easing: `cubic-bezier(${bezierParams})`,
        background_effect: 'animated',
      })),

      ...(transcript_effect &&
        transcriptSource.length > 0 && {
          transcript_effect,

          transcript_split:
            transcript_split || DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_split,

          transcript_placement:
            transcript_placement ||
            DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_placement,

          transcript_color:
            transcript_color || DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color,

          transcript_source: transcriptSource,
        }),
    };

    return json;
  }

  /**
   * `toJSON` for the renderer. Punctuation styles build rows with no width
   * limit, which the canvas wraps at spaces but not in spaceless full-width
   * text — so those rows get hard line breaks sized to this caption's box on
   * a canvas of `canvasSize`. A caption with user soft line breaks
   * (PLAT-11914) gets them as `\n` here too, on every style. Render payload
   * only: the stored JSON (and the clip's data) keep the unbroken row.
   */
  toRenderJSON(
    formatTime: Boolean,
    canvasSize: { width: number; height: number },
  ): Record<string, any> {
    const json = this.toJSON(formatTime);
    const config = this.karaokeConfig;
    const punctuationStyle =
      resolvePunctuationLineBreak(config.line_break_style) !== undefined;
    const softBreaks = this.hasSoftBreaks();
    if (!punctuationStyle && !softBreaks) return json;
    // Measure the word units even when no transcript effect sends them as
    // `transcript_source` — a plain-text caption needs the breaks in `text`.
    const hasTranscriptSource = Array.isArray(json.transcript_source);
    // Plain units, and the same units with the user's soft line breaks
    // (PLAT-11914) ending their lines in `\n`.
    const units: AnimationSourceUnit[] = hasTranscriptSource
      ? json.transcript_source
      : this.getTranscriptSourceForAnimation();
    const broken = softBreaks
      ? this.getTranscriptSourceForAnimation(true)
      : units;
    // Broken rows render in a wider box so their lines use the frame. The
    // user's lines are kept; under a Punctuation style each one that still
    // overflows wraps on its own.
    const width = punctuationStyle
      ? widenedCaptionBoxWidth(json.width)
      : json.width;
    const wrapped = punctuationStyle
      ? wrapWideAnimationLines(
          broken,
          wideCaptionLineCapacity({ ...config, width }, canvasSize) ??
            PUNCTUATION_WIDE_LINE_WIDTH,
        )
      : broken;
    // Units can already end in `\n` (paragraph breaks), so compare values.
    if (
      wrapped.length === units.length &&
      wrapped.every((unit, i) => unit.value === units[i].value)
    ) {
      return json;
    }
    return {
      ...json,
      width,
      text: insertUnitLineEnds(json.text, wrapped),
      ...(hasTranscriptSource && { transcript_source: wrapped }),
    };
  }
}
